import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTransaction } from '../store/slice/transactionSlice';
import AdvancedFilter from '../components/AdvancedFilter';

function TransactionsPage() {
  const {
    transactions,
    loading,
    error,
    fetchTransactions,
    createTransaction,
  } = useTransaction();

  const [amount, setAmount] = useState('');
  const [transactionType, setTransactionType] = useState('payment');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().slice(0, 10));
  const [clientName, setClientName] = useState('');
  const [service, setService] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    paymentMethod: 'all',
    dateFrom: '',
    dateTo: '',
    sortBy: '',
  });

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleFilterChange = (key, value) => {
    if (key === 'dateRange') {
      setFilters((p) => ({ ...p, dateFrom: value.from || '', dateTo: value.to || '' }));
    } else {
      setFilters((p) => ({ ...p, [key]: value }));
    }
  };

  const types = useMemo(() => [...new Set(transactions.map((t) => t.type).filter(Boolean))], [transactions]);
  const methods = useMemo(() => [...new Set(transactions.map((t) => t.paymentMethod).filter(Boolean))], [transactions]);

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'type',
      type: 'select',
      label: 'Type',
      value: filters.type,
      defaultValue: 'all',
      options: [...new Set(['payment', 'refund', 'adjustment', ...types])].map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })),
    },
    {
      key: 'paymentMethod',
      type: 'select',
      label: 'Payment Method',
      value: filters.paymentMethod,
      defaultValue: 'all',
      options: [...new Set(['cash', 'card', 'upi', 'other', ...methods])].map((m) => ({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) })),
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'Date Range',
      value: { from: filters.dateFrom, to: filters.dateTo },
    },
    {
      key: 'sortBy',
      type: 'sort',
      label: 'Sort By',
      value: filters.sortBy,
      defaultValue: '',
      options: [
        { value: 'date-desc', label: 'Date (newest first)' },
        { value: 'date-asc', label: 'Date (oldest first)' },
        { value: 'amount-desc', label: 'Amount (high to low)' },
        { value: 'amount-asc', label: 'Amount (low to high)' },
        { value: 'client-asc', label: 'Client (A-Z)' },
      ],
    },
  ];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.type && filters.type !== 'all') count++;
    if (filters.paymentMethod && filters.paymentMethod !== 'all') count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.sortBy) count++;
    return count;
  }, [filters]);

  const filteredTransactions = useMemo(() => {
    let result = [...transactions];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (t) =>
          (t.clientName || '').toLowerCase().includes(term) ||
          (t.service || '').toLowerCase().includes(term) ||
          (t.notes || '').toLowerCase().includes(term)
      );
    }
    if (filters.type && filters.type !== 'all') {
      result = result.filter((t) => t.type === filters.type);
    }
    if (filters.paymentMethod && filters.paymentMethod !== 'all') {
      result = result.filter((t) => t.paymentMethod === filters.paymentMethod);
    }
    if (filters.dateFrom || filters.dateTo) {
      result = result.filter((t) => {
        const d = new Date(t.date || t.createdAt);
        if (filters.dateFrom && d < new Date(filters.dateFrom)) return false;
        if (filters.dateTo && d > new Date(filters.dateTo + 'T23:59:59')) return false;
        return true;
      });
    }
    if (filters.sortBy) {
      const [field, order] = filters.sortBy.split('-');
      result.sort((a, b) => {
        let va = field === 'date' ? new Date(a.date || a.createdAt) : field === 'amount' ? parseFloat(a.amount) || 0 : (a[field === 'client' ? 'clientName' : field] || '').toLowerCase();
        let vb = field === 'date' ? new Date(b.date || b.createdAt) : field === 'amount' ? parseFloat(b.amount) || 0 : (b[field === 'client' ? 'clientName' : field] || '').toLowerCase();
        if (order === 'desc') [va, vb] = [vb, va];
        return va < vb ? -1 : va > vb ? 1 : 0;
      });
    }

    return result;
  }, [transactions, filters]);

  const totalRevenue = useMemo(
    () =>
      filteredTransactions.reduce((sum, t) => {
        const amt = parseFloat(t.amount) || 0;
        return sum + (t.type === 'refund' ? -amt : amt);
      }, 0),
    [filteredTransactions]
  );

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    if (amount && transactionDate) {
      const result = await createTransaction({
        amount: parseFloat(amount),
        type: transactionType,
        date: transactionDate,
        clientName: clientName || undefined,
        service: service || undefined,
        paymentMethod,
        notes: notes || undefined,
      });
      if (result?.meta?.requestStatus === 'fulfilled') {
        toast.success('Transaction added successfully');
      }
      setAmount('');
      setTransactionType('payment');
      setTransactionDate(new Date().toISOString().slice(0, 10));
      setClientName('');
      setService('');
      setNotes('');
      setShowForm(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString || '-';
    }
  };

  const formatCurrency = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '-';
    return `$${n.toFixed(2)}`;
  };

  const typeColor = (type) => {
    switch ((type || '').toLowerCase()) {
      case 'payment':
        return 'status-pill status-pill-success';
      case 'refund':
        return 'status-pill border-red-200 bg-red-50 text-red-700';
      case 'adjustment':
        return 'status-pill status-pill-warning';
      default:
        return 'status-pill status-pill-muted';
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading transactions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Transactions</h1>
          <p className="mt-1 text-slate-500 text-sm">View and manage payment transactions</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary inline-flex items-center gap-2"
        >
          <span>{showForm ? '−' : '+'}</span> {showForm ? 'Cancel' : 'Add Transaction'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAddTransaction} className="card-premium p-6 space-y-4 max-w-xl">
          <h3 className="font-semibold text-slate-800">Add New Transaction</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Amount *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input-premium"
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Type</label>
              <select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                className="input-premium"
                disabled={loading}
              >
                <option value="payment">Payment</option>
                <option value="refund">Refund</option>
                <option value="adjustment">Adjustment</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Date</label>
              <input
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                className="input-premium"
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="input-premium"
                disabled={loading}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">UPI</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Client Name</label>
              <input
                type="text"
                placeholder="Optional"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="input-premium"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Service</label>
              <input
                type="text"
                placeholder="Optional"
                value={service}
                onChange={(e) => setService(e.target.value)}
                className="input-premium"
                disabled={loading}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes</label>
            <textarea
              placeholder="Optional notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="2"
              className="input-premium resize-none"
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Adding...' : 'Add Transaction'}
          </button>
        </form>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total Transactions</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{transactions.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Filtered</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredTransactions.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Net Revenue (Filtered)</p>
          <p className={`mt-1 text-2xl font-bold ${totalRevenue >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(totalRevenue)}
          </p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search by client, service, or notes..."
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Client</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Service</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Method</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((tx) => (
                <tr key={tx.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                  <td className="py-4 px-6 text-slate-600">{formatDate(tx.date || tx.createdAt)}</td>
                  <td className="py-4 px-6 font-medium text-slate-800">{tx.clientName || '-'}</td>
                  <td className="py-4 px-6 text-slate-600">{tx.service || '-'}</td>
                  <td className="py-4 px-6">
                    <span className={`capitalize ${typeColor(tx.type)}`}>
                      {tx.type || 'payment'}
                    </span>
                  </td>
                  <td className={`py-4 px-6 text-right font-semibold ${tx.type === 'refund' ? 'text-red-600' : 'text-slate-800'}`}>
                    {tx.type === 'refund' ? '-' : ''}{formatCurrency(tx.amount)}
                  </td>
                  <td className="py-4 px-6 text-slate-600 capitalize">{tx.paymentMethod || '-'}</td>
                  <td className="py-4 px-6 text-slate-500 text-sm max-w-[200px] truncate" title={tx.notes}>{tx.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredTransactions.length === 0 && (
          <div className="py-16 text-center text-slate-500">No transactions found. Add one to get started!</div>
        )}
      </div>
    </div>
  );
}

export default TransactionsPage;
